import Map, {
  FullscreenControl,
  GeolocateControl,
  Layer,
  NavigationControl,
  Popup,
  Source,
} from "react-map-gl";
import { unclusteredPointLayer, blackPinLayer } from "./layers";
import "mapbox-gl/dist/mapbox-gl.css";
import styles from "./map-box.module.scss";
import { ClassificationProps } from "./map-box.interface";
import React, { useEffect, useState } from "react";
import {
  formatAbbreviatedCurrency,
  formatCurrency,
  dateMonthFormat,
  latVar,
  StatusObjectColorFilter,
} from "../../../utils/utils";

import PropertyListCard from "../../common/property-list-card";
import useProperty from "../../../hooks/context/useProperty";
// import { useMapPublishProperties } from "../../../hooks/react-query/properties/useMapPublishPropertyList";\
import MobilePropertiesCard from "../../common/mobile-properties-card";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import moment from "moment";
import { NavigateToExchange } from "../../../utils/inv-exch-amplitude";
import { MapPinClicked } from "../../investor/amplitude";
import { Tooltip } from "primereact/tooltip";
import PrePinsToggle from "../../common/pre-pins-toggle";
import MapPrePinSidebar from "../../common/map-prepin-sidebar";
// import { useMapInvestorPropertyList } from "../../../hooks/react-query/investor/useMapInvestorPropertyList";

const MapBox = (props: ClassificationProps) => {
  const location = useLocation();
  const getPersistedMapState = () => {
    const map = localStorage.getItem("map-persist");
    return map
      ? JSON.parse(map)
      : {
          lat: latVar.lat,
          lng: latVar.lng,
          zoom: latVar.zoom,
        };
  };

  const persistedMapState = getPersistedMapState();

  const [searchParams, setSearchParams] = useSearchParams();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [initialCoords, setInitialCoords] = useState(() => ({
    latitude: searchParams.get("lat") || persistedMapState.lat || latVar.lat,
    longitude: searchParams.get("lng") || persistedMapState.lng || latVar.lng,
    zoom: searchParams.get("zoom") || persistedMapState.zoom || latVar.zoom,
  }));
  const navigate = useNavigate();
  const { setCoords, hoveredProperty, setHoveredProperty }: any = useProperty();
  const prevBoundsRef = React.useRef({
    topLeftLng: 0,
    topLeftLat: 0,
    bottomRightLng: 0,
    bottomRightLat: 0,
  });

  const [propertiesArr, setPropertiesArr] = useState<any>(() => {
    return {
      type: "FeatureCollection",
      features: [],
    };
  });

  const [blackPinsArr, setBlackPinsArr] = useState<any>(() => {
    return {
      type: "FeatureCollection",
      features: [],
    };
  });

  const [styleLoaded, setStyleLoaded] = React.useState(false);
  const mapRef: any = React.useRef<any>(null);
  const [popupInfo, setPopupInfo] = useState<any>(null);
  const [blackPinPopup, setBlackPinPopup] = useState<{
    id?: string;
    longitude: number;
    latitude: number;
    acctName: string;
    address: string;
    askingPrice?: number; // This is the sold price
    purchasePrice?: number;
    currentSaleRecordingDate?: string | number;
    totalSold?: string;
    totalPurchased?: string;
  } | null>(null);
  const [activePopupType, setActivePopupType] = useState<
    "property" | "blackPin" | null
  >(null);

  // Initialize showPre from URL params, but force OFF for investors
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const [showPre, setShowPre] = useState<boolean>(() => {
    if (props.type === "investor") return false;
    const showPreParam = searchParams.get("showPre");
    return showPreParam === null ? false : showPreParam === "true";
  });
  const [showPrePinsMobileSidebar, setShowPrePinsMobileSidebar] =
    useState(false);

  // Update URL params when showPre changes - only for agents
  useEffect(() => {
    if (props.type !== "agent") return;

    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("showPre", String(showPre));
    setSearchParams(updatedParams, { replace: true });
  }, [showPre, searchParams, setSearchParams, props.type]);

  // Clear black pin popup when showPre is toggled off
  useEffect(() => {
    if (!showPre && activePopupType === "blackPin") {
      setBlackPinPopup(null);
      setActivePopupType(null);
    }
  }, [showPre, activePopupType]);

  useEffect(() => {
    // Only update popup info if we're showing a property popup
    if (popupInfo && activePopupType === "property") {
      // Handle different data structures: investor has data: [], agent has data: { properties: [] }
      const properties =
        props.type === "investor"
          ? props?.arrMapProperty?.data
          : props?.arrMapProperty?.data?.properties;

      const filteredPopInfo = properties?.filter(
        (item: any) => item.id === popupInfo.id
      );

      if (filteredPopInfo && filteredPopInfo.length > 0) {
        setPopupInfo(filteredPopInfo[0]);
      }
    }
  }, [props?.arrMapProperty, activePopupType, props.type]);

  // Update black pin popup info when pres data changes - only for agents
  useEffect(() => {
    // Skip for investors
    if (props.type !== "agent") return;

    // Only update black pin popup if we're showing a black pin popup
    if (blackPinPopup && activePopupType === "blackPin") {
      const filteredBlackPinInfo = props?.arrMapProperty?.data?.pres?.find(
        (item: any) => {
          // Match by id if available, otherwise match by coordinates
          if (blackPinPopup.id) {
            return String(item.id) === String(blackPinPopup.id);
          }
          return (
            item.latitude === blackPinPopup.latitude &&
            item.longitude === blackPinPopup.longitude
          );
        }
      );

      if (filteredBlackPinInfo) {
        setBlackPinPopup({
          id: filteredBlackPinInfo?.id,
          longitude: blackPinPopup.longitude,
          latitude: blackPinPopup.latitude,
          acctName: filteredBlackPinInfo?.acctName || "N/A",
          address:
            filteredBlackPinInfo?.address ||
            filteredBlackPinInfo?.fullAddress ||
            `${filteredBlackPinInfo?.street || ""}, ${
              filteredBlackPinInfo?.city || ""
            }, ${filteredBlackPinInfo?.state || ""} ${
              filteredBlackPinInfo?.postalcode || ""
            }`.trim(),
          askingPrice: filteredBlackPinInfo?.askingPrice,
          purchasePrice: filteredBlackPinInfo?.purchasePrice,
          currentSaleRecordingDate:
            filteredBlackPinInfo?.currentSaleRecordingDate ||
            filteredBlackPinInfo?.date,
          totalSold: filteredBlackPinInfo?.totalSold,
          totalPurchased: filteredBlackPinInfo?.totalPurchased,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.arrMapProperty?.data?.pres, activePopupType, props.type]);

  const onClick = (event: any) => {
    event.preventDefault();
    const feature = event?.features[0];
    if (!feature?.properties) return;

    // Check which layer was clicked by examining the layer ID
    const clickedLayerId = feature?.layer?.id;

    // If black pin layer was clicked, show only simple popup (only for agents)
    if (clickedLayerId === "black-pin-layer" && props.type === "agent") {
      const blackPinData = props?.arrMapProperty?.data?.pres?.find(
        (item: any) => String(item.id) === String(feature?.properties?.id)
      );

      if (blackPinData) {
        const newBlackPinPopup = {
          id: blackPinData?.id,
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          acctName: blackPinData?.acctName || "N/A",
          address:
            blackPinData?.address ||
            blackPinData?.fullAddress ||
            `${blackPinData?.street || ""}, ${blackPinData?.city || ""}, ${
              blackPinData?.state || ""
            } ${blackPinData?.postalcode || ""}`.trim(),
          askingPrice: blackPinData?.askingPrice,
          purchasePrice: blackPinData?.purchasePrice,
          currentSaleRecordingDate:
            blackPinData?.currentSaleRecordingDate || blackPinData?.date,
          totalSold: blackPinData?.totalSold,
          totalPurchased: blackPinData?.totalPurchased,
        };
        // Clear any existing popups first
        setPopupInfo(null);
        setBlackPinPopup(null);
        setActivePopupType(null);
        // Set new black pin popup after a brief delay to ensure clean state
        setTimeout(() => {
          setBlackPinPopup(newBlackPinPopup);
          setActivePopupType("blackPin");
        }, 0);
      }
    } else if (clickedLayerId === "unclustered-point") {
      // Show detailed popup for regular pins only
      // Handle different data structures: investor has data: [], agent has data: { properties: [] }
      const properties =
        props.type === "investor"
          ? props?.arrMapProperty?.data
          : props?.arrMapProperty?.data?.properties;

      const matchingProperty = properties?.find(
        (item: any) => item.id === feature?.properties?.id
      );

      if (matchingProperty) {
        const newPopupInfo = {
          ...feature?.properties,
          ...matchingProperty, // Spread the complete property data
          acquisitionAgent1:
            feature?.properties.acquisitionAgent1 &&
            JSON.parse(feature?.properties?.acquisitionAgent1),
        };
        if (props.type === "investor") {
          MapPinClicked({
            propertyId: newPopupInfo?.id,
            propertySFID: newPopupInfo?.propertySFID,
            opportunitySFID: newPopupInfo?.opportunitySFID,
            pubPropertySFID: newPopupInfo?.pubPropertySFID,
            shareId: newPopupInfo?.shareKey,
          });
        }
        // Clear any existing popups first
        setBlackPinPopup(null);
        setPopupInfo(null);
        setActivePopupType(null);
        // Set new property popup after a brief delay to ensure clean state
        setTimeout(() => {
          setPopupInfo(newPopupInfo);
          setActivePopupType("property");
        }, 0);
      }
    }
  };

  React.useEffect(() => {
    // Handle different data structures: investor has data: [], agent has data: { properties: [] }
    const properties =
      props.type === "investor"
        ? props?.arrMapProperty?.data
        : props?.arrMapProperty?.data?.properties;

    if (properties && properties?.length > 0) {
      setPropertiesArr(() => {
        return {
          type: "FeatureCollection",
          features:
            properties?.map((item: any) => ({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [item.longitude, item.latitude, 0],
              },
              properties: {
                ...item,
                key: JSON.stringify({
                  lat: item?.latitude,
                  lng: item?.longitude,
                }),
                id: item?.id,
                address: item?.address,
                lng: item?.longitude,
                lat: item?.latitude,
                icon: "custom-marker",
                sortKey: Number(item?.askingPrice) + 1000000000000, // Higher sortKey to render on top
                price: `${formatAbbreviatedCurrency(item?.askingPrice, "USD")}`,
                offerDueDateAndTime: item?.offerDueDateAndTime,
              },
            })) || [],
        };
      });
    }
    // Don't reset to empty - keep existing pins visible until new data loads
  }, [props?.arrMapProperty?.data, searchParams, props.type]);

  React.useEffect(() => {
    // Only process black pins for agents when showPre is true
    if (
      props.type === "agent" &&
      showPre &&
      props?.arrMapProperty?.data?.pres &&
      props?.arrMapProperty?.data?.pres?.length > 0
    ) {
      setBlackPinsArr(() => {
        return {
          type: "FeatureCollection",
          features:
            props?.arrMapProperty?.data?.pres?.map((item: any) => {
              // Determine icon based on totalSold and totalPurchased
              // If totalSold > 0, property is sold (red marker)
              // If totalPurchased > 0, property is purchased (black marker)
              let icon = "black-marker"; // default
              const totalSold = Number(item?.totalSold) || 0;
              const totalPurchased = Number(item?.totalPurchased) || 0;

              if (totalSold > 0) {
                icon = "red-marker"; // Sold pins are red
              } else if (totalPurchased > 0) {
                icon = "black-marker"; // Purchased pins are black
              }

              return {
                type: "Feature",
                geometry: {
                  type: "Point",
                  coordinates: [item.longitude, item.latitude, 0],
                },
                properties: {
                  ...item,
                  key: JSON.stringify({
                    lat: item?.latitude,
                    lng: item?.longitude,
                  }),
                  id: item?.id,
                  address: item?.address,
                  lng: item?.longitude,
                  lat: item?.latitude,
                  icon: icon,
                  sortKey: Number(item?.askingPrice),
                  price: item?.askingPrice
                    ? `${formatAbbreviatedCurrency(item?.askingPrice, "USD")}`
                    : "$0",
                  offerDueDateAndTime: item?.offerDueDateAndTime,
                },
              };
            }) || [],
        };
      });
    } else if (!showPre || props.type === "investor") {
      // Clear black pins when showPre is false or user is investor
      setBlackPinsArr({
        type: "FeatureCollection",
        features: [],
      });
    }
    // Don't reset to empty - keep existing pins visible until new data loads
  }, [props?.arrMapProperty?.data?.pres, searchParams, showPre, props.type]);

  const onMapLoad = React.useCallback(
    (event: any) => {
      const map = event?.target;
      if (!map) return;
      map?.on("style.load", () => {
        map?.on("load", () => {
          map?.loadImage("/images/pin.png", (error: any, image: any) => {
            if (error) {
              console.error("Error loading pin image:", error);
              return;
            }
            if (!map.hasImage("custom-marker")) {
              map.addImage("custom-marker", image);
            }
          });
          map?.loadImage("/images/pin-black.png", (error: any, image: any) => {
            if (error) {
              console.error("Error loading pin image:", error);
              return;
            }
            if (!map.hasImage("black-marker")) {
              map.addImage("black-marker", image);
            }
          });
          map?.loadImage("/images/pin-red.png", (error: any, image: any) => {
            if (error) {
              console.error("Error loading red pin image:", error);
              return;
            }
            if (!map.hasImage("red-marker")) {
              map.addImage("red-marker", image);
            }
          });
          map?.loadImage("/images/pin-hover.png", (error: any, image: any) => {
            if (error) {
              console.error("Error loading pin image:", error);
              return;
            }
            if (!map.hasImage("highlighted-marker")) {
              map.addImage("highlighted-marker", image);
            }
          });
          map?.on("mouseenter", "unclustered-point", (e: any) => {
            map.getCanvas().style.cursor = "pointer";
            const feature = e?.features[0];
            if (feature) {
              setHoveredProperty(feature?.properties);
            }
          });
          map?.on("mouseleave", "unclustered-point", () => {
            map.getCanvas().style.cursor = "";
            setHoveredProperty({});
          });
          map?.on("mouseenter", "black-pin-layer", (e: any) => {
            map.getCanvas().style.cursor = "pointer";
            // Don't set hovered property for black pins to avoid confusion with regular pins
          });
          map?.on("mouseleave", "black-pin-layer", () => {
            map.getCanvas().style.cursor = "";
          });
          setStyleLoaded(true);
        });
      });
    },
    [props?.arrMapProperty?.data, searchParams]
  );

  const handleMapMoveEnd = () => {
    // setPopupInfo(null);
    const { lat, lng } = mapRef?.current?.getCenter();
    const zoom = mapRef?.current?.getZoom();
    if (!lat && !lng && !zoom) return;
    const updatedParams = new URLSearchParams(location.search);

    updatedParams.set("lat", lat);
    updatedParams.set("lng", lng);
    updatedParams.set("zoom", zoom);
    localStorage.setItem(
      "map-persist",
      JSON.stringify({ lat: lat, lng: lng, zoom: zoom })
    );
    setSearchParams(searchParams);
    handleGetBounds();
  };

  useEffect(() => {
    if (props.showMap && mapRef?.current) {
      mapRef?.current?.resize();
    }
  }, [props.showMap]);

  const handleGetBounds = React.useCallback(() => {
    if (mapRef?.current) {
      const searchParams = new URLSearchParams(location.search);
      // const searchTextValue = searchParams.get("searchText");
      const bounds = mapRef?.current?.getBounds();
      const topLeft = bounds.getNorthWest();
      const bottomRight = bounds.getSouthEast();

      const currentBounds = {
        topLeftLng: topLeft.lng,
        topLeftLat: topLeft.lat,
        bottomRightLat: bottomRight.lat,
        bottomRightLng: bottomRight.lng,
      };
      const center = mapRef?.current?.getCenter();

      searchParams.set(
        "lat",
        persistedMapState.lat || center.lat || latVar.lat
      );
      searchParams.set(
        "lng",
        persistedMapState.lng || center.lng || latVar.lng
      );
      searchParams.set(
        "zoom",
        persistedMapState.zoom || center?.zoom || latVar.zoom
      );

      window.history.replaceState(
        null,
        "",
        `${location.pathname}?${searchParams.toString()}`
      );

      const hasBoundsChanged =
        currentBounds.topLeftLng !== prevBoundsRef.current.topLeftLng ||
        currentBounds.topLeftLat !== prevBoundsRef.current.topLeftLat ||
        currentBounds.bottomRightLng !== prevBoundsRef.current.bottomRightLng ||
        currentBounds.bottomRightLat !== prevBoundsRef.current.bottomRightLat;

      if (hasBoundsChanged) {
        prevBoundsRef.current = currentBounds;
        setCoords({
          topLeftLng: topLeft.lng,
          topLeftLat: topLeft.lat,
          bottomRightLat: bottomRight.lat,
          bottomRightLng: bottomRight.lng,
        });
      }
    }
  }, [props?.arrMapProperty?.data, initialCoords]);

  React.useEffect(() => {
    if (
      mapRef?.current &&
      styleLoaded &&
      props?.arrMapProperty?.data?.properties &&
      props?.arrMapProperty?.data?.properties?.length > 0 &&
      mapRef?.current?.getSource("earthquakes")
    ) {
      mapRef?.current?.flyTo({
        center: [initialCoords?.longitude, initialCoords?.latitude],
        zoom: initialCoords?.zoom,
        essential: true,
      });
    }
  }, [initialCoords, styleLoaded, searchParams]);

  React.useEffect(() => {
    // Handle different data structures: investor has data: [], agent has data: { properties: [] }
    const properties =
      props.type === "investor"
        ? props?.arrMapProperty?.data
        : props?.arrMapProperty?.data?.properties;

    if (
      styleLoaded &&
      mapRef?.current &&
      properties &&
      properties?.length > 0 &&
      mapRef?.current?.getSource("earthquakes")
    ) {
      const updatedFeatures = properties?.map((item: any) => ({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [item?.longitude, item?.latitude, 0],
        },
        properties: {
          ...item,
          icon:
            item.id === hoveredProperty?.id
              ? "highlighted-marker"
              : "custom-marker",
          price: `${formatAbbreviatedCurrency(item.askingPrice, "USD")}`,
          sortKey: Number(item?.askingPrice) + 1000000000, // Higher sortKey to render on top
          offerDueDateAndTime: item?.offerDueDateAndTime,
        },
      }));
      mapRef?.current?.getSource("earthquakes")?.setData({
        type: "FeatureCollection",
        features: updatedFeatures,
      });
    }

    // Update black pins on hover - only for agents
    if (
      props.type === "agent" &&
      styleLoaded &&
      mapRef?.current &&
      props?.arrMapProperty?.data?.pres &&
      props?.arrMapProperty?.data?.pres?.length > 0 &&
      mapRef?.current?.getSource("blackPins") &&
      showPre
    ) {
      const updatedBlackPins = props?.arrMapProperty?.data?.pres?.map(
        (item: any) => {
          // Determine icon based on totalSold and totalPurchased
          // If totalSold > 0, property is sold (red marker)
          // If totalPurchased > 0, property is purchased (black marker)
          let icon = "black-marker"; // default
          const totalSold = Number(item?.totalSold) || 0;
          const totalPurchased = Number(item?.totalPurchased) || 0;

          if (totalSold > 0) {
            icon = "red-marker"; // Sold pins are red
          } else if (totalPurchased > 0) {
            icon = "black-marker"; // Purchased pins are black
          }

          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [item?.longitude, item?.latitude, 0],
            },
            properties: {
              ...item,
              icon: icon,
              price: item.askingPrice
                ? `${formatAbbreviatedCurrency(item.askingPrice, "USD")}`
                : "$0",
              sortKey: Number(item?.askingPrice),
              offerDueDateAndTime: item?.offerDueDateAndTime,
            },
          };
        }
      );
      mapRef?.current?.getSource("blackPins")?.setData({
        type: "FeatureCollection",
        features: updatedBlackPins,
      });
    }
  }, [
    hoveredProperty,
    searchParams,
    props?.arrMapProperty?.data,
    styleLoaded,
    showPre,
    props.type,
  ]);

  // Helper function to parse date from YYYYMMDD format (API may return number or string)
  const parseRecordingDate = (dateVal: string | number | null | undefined) => {
    if (dateVal === null || dateVal === undefined || dateVal === "") return null;
    const dateStr = String(dateVal);
    if (!/^\d{8}$/.test(dateStr)) return null;
    // Parse YYYYMMDD format (e.g., "20230919")
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    const formattedDate = `${year}-${month}-${day}`;
    return dateMonthFormat(formattedDate);
  };

  const checkActivationTime = () => {
    if (popupInfo?.activatedAt === null) return false;
    return (
      moment.utc().diff(moment.utc(popupInfo?.activatedAt), "minutes") >=
      +`${process.env.REACT_APP_ACTIVE_TIMER}`
    );
  };

  const [showIEButton, setShowIEButton] = useState(checkActivationTime);

  useEffect(() => {
    // Initial check
    setShowIEButton(checkActivationTime());

    // Set up interval to check every 5 minutes
    const interval = setInterval(() => {
      setShowIEButton(checkActivationTime());
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [popupInfo?.activatedAt]);
  return (
    <div
      className={`${styles.mapWrap} ${props?.showMap ? styles.fullWidth : ""}`}
    >
      <div className={`${styles.mapBox}`}>
        {props.type === "agent" && !isMobile && (
          <PrePinsToggle showPre={showPre} onToggle={setShowPre} />
        )}
        {props.type === "agent" && isMobile && (
          <button
            className={styles.mobilePrePinsButton}
            onClick={() => setShowPrePinsMobileSidebar(true)}
            aria-label="PRE pins"
          >
            <img
              src="/images/location.png"
              alt="PRE pins"
              width={16}
              height={16}
            />
            <span>PRE pins</span>
          </button>
        )}
        
        {props.type === "agent" && isMobile && (
          <MapPrePinSidebar
            showSidebar={showPrePinsMobileSidebar}
            setShowSidebar={setShowPrePinsMobileSidebar}
            showPre={showPre}
            setShowPre={setShowPre}
          />
        )}

        <ErrorBoundary fallback={<div>Something went wrong</div>}>
          <Map
            initialViewState={{
              latitude: Number(initialCoords?.latitude) || latVar.lat,
              longitude: Number(initialCoords?.longitude) || latVar.lng,
              zoom: Number(initialCoords?.zoom) || latVar.zoom,
            }}
            mapStyle={"mapbox://styles/mapbox/streets-v12"}
            mapboxAccessToken={process.env.REACT_APP_MAP_KEY}
            interactiveLayerIds={[unclusteredPointLayer?.id, blackPinLayer?.id]}
            onClick={onClick}
            ref={mapRef}
            onLoad={onMapLoad}
            onStyleData={onMapLoad}
            onMoveEnd={handleMapMoveEnd}
            onZoomEnd={handleMapMoveEnd}
          >
            {styleLoaded && (
              <>
                <GeolocateControl />
                <FullscreenControl />
                <NavigationControl />
                {/* Render black pins first so they appear below regular pins - only for agents */}
                {props.type === "agent" && showPre && (
                  <Source
                    id="blackPins"
                    type="geojson"
                    data={blackPinsArr || []}
                  >
                    <Layer {...blackPinLayer} />
                  </Source>
                )}
                <Source
                  id="earthquakes"
                  type="geojson"
                  data={propertiesArr || []}
                >
                  <Layer {...unclusteredPointLayer} />
                </Source>
              </>
            )}
            {props.type === "agent" &&
              blackPinPopup &&
              activePopupType === "blackPin" && (
                <Popup
                  longitude={blackPinPopup.longitude}
                  latitude={blackPinPopup.latitude}
                  closeOnClick={true}
                  onClose={() => {
                    setBlackPinPopup(null);
                    setActivePopupType(null);
                  }}
                  className={styles.mapPopup}
                >
                  <div className={styles.blackPinPopupContent}>
                    <div
                      className={styles.accName}
                      onClick={() => {
                        if (blackPinPopup.acctName) {
                          navigator.clipboard
                            .writeText(blackPinPopup.acctName)
                            .then(() => {})
                            .catch((err) => {
                              console.error("Failed to copy: ", err);
                            });
                        }
                      }}
                    >
                      <img src="/images/user.svg" alt="" />
                      {blackPinPopup.acctName}
                    </div>
                    <div className={styles.divider}></div>
                    <div className={styles.address}>
                      <img src="/images/location.svg" alt="" />
                      {blackPinPopup.address}
                    </div>
                    {(blackPinPopup.askingPrice ||
                      blackPinPopup.purchasePrice ||
                      blackPinPopup.currentSaleRecordingDate) && (
                      <>
                        <div className={styles.divider}></div>
                        <div className={styles.priceDetails}>
                          {blackPinPopup.askingPrice !== undefined &&
                            blackPinPopup.askingPrice !== null && (
                              <div className={styles.priceRow}>
                                <span className={styles.priceLabel}>
                                  Sale Price:
                                </span>
                                <span className={styles.priceValue}>
                                  {formatCurrency(
                                    blackPinPopup.askingPrice,
                                    "USD",
                                    "en-US"
                                  )}
                                </span>
                              </div>
                            )}
                          {blackPinPopup.purchasePrice !== undefined &&
                            blackPinPopup.purchasePrice !== null && (
                              <div className={styles.priceRow}>
                                <span className={styles.priceLabel}>
                                  Purchase Price:
                                </span>
                                <span className={styles.priceValue}>
                                  {formatCurrency(
                                    blackPinPopup.purchasePrice,
                                    "USD",
                                    "en-US"
                                  )}
                                </span>
                              </div>
                            )}
                          {(() => {
                            const saleDate = parseRecordingDate(
                              blackPinPopup.currentSaleRecordingDate
                            );
                            return saleDate ? (
                              <div className={styles.priceRow}>
                                <span className={styles.priceLabel}>
                                  Sale Date:
                                </span>
                                <span className={styles.priceValue}>
                                  {saleDate}
                                </span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </>
                    )}
                  </div>
                </Popup>
              )}
            {popupInfo?.longitude &&
              popupInfo?.latitude &&
              activePopupType === "property" && (
                <Popup
                  longitude={Number(popupInfo?.longitude || popupInfo?.lng)}
                  latitude={Number(popupInfo?.latitude || popupInfo?.lat)}
                  style={{
                    width: window?.innerWidth <= 500 ? "250px" : "370px",
                  }}
                  className={styles.popupContainer}
                  onClose={() => {
                    setPopupInfo(null);
                    setActivePopupType(null);
                  }}
                >
                  <div className={styles.popup}>
                    <PropertyListCard
                      loginType={props.type}
                      sfid={popupInfo?.sfid}
                      id={popupInfo?.id}
                      lat={popupInfo?.latitude || popupInfo?.lat}
                      lng={popupInfo?.longitude || popupInfo?.lng}
                      type={"popup"}
                      className={styles.popupCard}
                      totalBath={popupInfo?.baths || popupInfo?.totalBath}
                      sqft={popupInfo?.sqft}
                      bedRooms={popupInfo?.bedRooms}
                      imageUrl={popupInfo.posterImage}
                      shape={"circle"}
                      listedTitle={"Listed By"}
                      defaultProfile={
                        popupInfo?.acquisitionAgent1?.smallBannerPhotoUrl
                      }
                      agentFirstName={
                        popupInfo?.acquisitionAgent1?.name?.split(" ")[0]
                      }
                      agentLastName={
                        popupInfo?.acquisitionAgent1?.name?.split(" ")[1]
                      }
                      salesInstruction={popupInfo?.salesInstruction}
                      street={popupInfo?.street}
                      location={popupInfo?.location}
                      amount={popupInfo.askingPrice}
                      currencyCode={"USD"}
                      locale={"en-US"}
                      propertyId={popupInfo?.id}
                      park={popupInfo?.park}
                      property={popupInfo}
                      city={popupInfo?.city}
                      state={popupInfo?.state}
                      postalCode={popupInfo?.postalcode}
                      favCount={popupInfo?.favCount}
                      status={StatusObjectColorFilter(popupInfo?.status)}
                      statusValue={popupInfo?.status}
                      activatedAt={popupInfo?.activatedAt}
                      shareKey={popupInfo?.shareKey}
                      offerDueDateAndTime={popupInfo?.offerDueDateAndTime}
                      opportunitySfid={popupInfo?.opportunitySFID}
                      pubPropertySfid={popupInfo?.pubPropertySFID}
                      objPropertyDetails={popupInfo}
                    />
                    <MobilePropertiesCard
                      isPlaceholderData={false}
                      item={popupInfo}
                      status={StatusObjectColorFilter(popupInfo?.status)}
                      type={"popup"}
                      loginType={props.type}
                    />
                    {props.type === "agent" && (
                      <>
                        {popupInfo?.status === "Active" && (
                          <div
                            className={`exchange-${popupInfo.id} ${
                              styles.findInvestor
                            } ${
                              checkActivationTime() ? styles.openButton : ""
                            }`}
                            onClick={() => {
                              navigate(
                                `/flipper-vault?pid=${popupInfo.id}&sfid=${
                                  popupInfo?.sfid
                                }&lat=${
                                  popupInfo?.latitude || popupInfo?.lat
                                }&lng=${
                                  popupInfo?.longitude || popupInfo?.lng
                                }&radius=60&shouldIgnoreRadius=true&price=${
                                  popupInfo?.askingPrice
                                }&street=${encodeURIComponent(popupInfo?.street || "")}&city=${encodeURIComponent(
                                  popupInfo?.city || ""
                                )}&state=${encodeURIComponent(popupInfo?.state || "")}&postalCode=${encodeURIComponent(
                                  popupInfo?.postalcode || ""
                                )}&order=High+to+Low&showIEButton=${showIEButton}&view=ALL+ACCOUNTS`
                              );
                              NavigateToExchange({
                                property_id: popupInfo?.id || "",
                                property_sfid: popupInfo?.sfid,
                                property_address: `${popupInfo?.street}, ${popupInfo?.city}, ${popupInfo?.state} ${popupInfo?.postalCode}`,
                              });
                            }}
                          >
                            <Tooltip
                              target={`.exchange-${popupInfo?.id}`}
                              className={styles.tooltip}
                              content={moment(popupInfo?.activatedAt)
                                .utc()
                                .add(48, "hours")
                                .local()
                                .format("MM/DD/YY hh:mm A")}
                            />
                            <span>Investor Exchange </span>
                            <img
                              src="/images/exchange.svg"
                              alt=""
                              width={25}
                              height={25}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </Popup>
              )}
          </Map>
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default MapBox;
